// Utilidades de contraste WCAG 2.x. Todas puras: color in → valor out.
// Existen porque el acento es personalizable por el usuario (Preferencias admite
// cualquier hex): el texto que va SOBRE el acento no puede ser un valor fijo,
// se elige por luminancia para cumplir contraste AA (≥4.5:1) con cualquier color.

const WHITE = '#ffffff';
// Tinta oscura para acentos claros. Slate casi negro: gana ≥4.5:1 sobre todos los
// acentos donde el blanco falla (naranjas, turquesas, verdes claros).
const DARK_INK = '#0f172a';
const ADJUST_STEPS = 20; // granularidad del ajuste de adjustForContrast

function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace('#', '');
  return [
    parseInt(clean.slice(0, 2), 16),
    parseInt(clean.slice(2, 4), 16),
    parseInt(clean.slice(4, 6), 16),
  ];
}

function rgbToHex(red: number, green: number, blue: number): string {
  const channel = (value: number) => Math.round(value).toString(16).padStart(2, '0');
  return `#${channel(red)}${channel(green)}${channel(blue)}`;
}

function channelToLinear(value: number): number {
  const c = value / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

export function relativeLuminance(hex: string): number {
  const [red, green, blue] = hexToRgb(hex);
  return 0.2126 * channelToLinear(red) + 0.7152 * channelToLinear(green) + 0.0722 * channelToLinear(blue);
}

export function contrastRatio(colorA: string, colorB: string): number {
  const luminanceA = relativeLuminance(colorA);
  const luminanceB = relativeLuminance(colorB);
  const lighter = Math.max(luminanceA, luminanceB);
  const darker = Math.min(luminanceA, luminanceB);
  return (lighter + 0.05) / (darker + 0.05);
}

// Blanco o tinta oscura: el que MÁS contraste tenga sobre `background`.
// Para el texto de CTAs/chips cuyo fondo es el acento (fijo o elegido por el usuario).
export function bestTextOn(background: string): string {
  return contrastRatio(WHITE, background) >= contrastRatio(DARK_INK, background) ? WHITE : DARK_INK;
}

// Ajusta `hex` para usarlo como TEXTO sobre `background`: lo oscurece si el fondo es
// claro (o lo aclara si es oscuro) hasta alcanzar el contraste pedido. Si ya cumple,
// lo devuelve intacto. Determinística: mismo input → mismo output.
export function adjustForContrast(hex: string, background: string, target = 4.5): string {
  if (contrastRatio(hex, background) >= target) return hex;
  const backgroundIsLight = relativeLuminance(background) >= 0.5;
  const goal = backgroundIsLight ? 0 : 255; // oscurecer sobre claro, aclarar sobre oscuro
  const [red, green, blue] = hexToRgb(hex);
  for (let step = 1; step <= ADJUST_STEPS; step++) {
    const mix = step / ADJUST_STEPS;
    const candidate = rgbToHex(
      red + (goal - red) * mix,
      green + (goal - green) * mix,
      blue + (goal - blue) * mix,
    );
    if (contrastRatio(candidate, background) >= target) return candidate;
  }
  return backgroundIsLight ? '#000000' : WHITE;
}
